import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { Input } from '../../../../../widgets/input';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { commitMutation, fetchQuery, useLazyLoadQuery } from 'react-relay/hooks';
import environment from 'src/relay/environment';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { deleteLibraryMaterialMutation } from './query/deleteLibraryMaterialMutation';
import { libraryMaterialQuery } from './query/libraryMaterialQuery';
import { libraryMaterialsQuery } from './query/materialsLibraryQuery';

const MaterialsListContainer = styled.div`
    border: 1px ${(props) => props.theme.app.primary_light} solid;
    height: 400px;
    border-radius: 3px;
    overflow: auto;
    padding: 10px;
    margin: 10px 0;
`;

const MaterialItem = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    padding: 10px;
    margin: 5px 0;
    border-radius: 5px;
    cursor: pointer;
    background-color: ${(props) => (props.$selected ? props.theme.app.primary_light : 'transparent')};
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.primary : 'transparent')};

    &:hover {
        background-color: ${(props) => props.theme.app.primary_light};
    }
`;

const MaterialPreviewImage = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 3px;
    margin-right: 15px;
    object-fit: cover;
    background-color: ${(props) => props.theme.app.primary_light};
`;

const MaterialName = styled.span`
    font-size: 14px;
    color: ${(props) => props.theme.input.color};
`;

const SearchContainer = styled.div`
    margin: 10px 0;
`;

const SearchInput = styled(Input)`
    width: 100%;
    padding: 8px;
    border: 1px solid ${(props) => props.theme.app.primary_light};
    border-radius: 3px;
    font-size: 14px;
`;

interface LibraryMaterial {
    id: string;
    materialID: string;
    tempMaterialID: string;
    name: string;
    json: string;
    url: string;
    rootUrl: string;
    materialFileName: string;
    sellerID: number;
    imageUrl: string | null;
    imageData: string | null;
    hasShaders: boolean;
    sourceTextureImages: Array<{
        sceneID: string;
        sourceID: string;
        materialID: string;
        url: string;
        fileLength: number;
    }>;
    textureImages: Array<{
        textureImageID: string;
        imageUrl: string;
        imageFileLength: number;
    }>;
    shaders: {
        materialID: string;
        name: string;
        vertex: string;
        fragment: string;
    } | null;
    shaderInputs: Array<{
        inputID: string;
        name: string;
        shaderInputTypeID: number;
    }>;
    shaderOptions: Array<{
        optionID: string;
        name: string;
        shaderOptionTypeID: number;
    }>;
}

interface MaterialImportDialogProps {
    sceneEditor?: any;
    onImport: (selectedMaterials: LibraryMaterial[]) => void;
    onMaterialsUpdated?: (materials: any[]) => void;
}

const MaterialImportDialog: FC<MaterialImportDialogProps> = ({ sceneEditor, onImport, onMaterialsUpdated }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());
    const [isImporting, setIsImporting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0); // Per forzare il refresh della query
    const [deletedMaterialIds, setDeletedMaterialIds] = useState<Set<string>>(new Set());

    // Aggiorniamo quando onMaterialsUpdated viene chiamato
    useEffect(() => {
        // Forza un re-render quando vengono aggiornati i materiali
        // Questo forza Relay a rifare la query
        setSelectedMaterials(new Set());
    }, [onMaterialsUpdated]);

    // Query per ottenere i materiali della libreria - con refreshKey per forzare refresh
    const data = useLazyLoadQuery<any>(libraryMaterialsQuery, { refreshKey }, { fetchPolicy: 'network-only' });
    const libraryMaterials: LibraryMaterial[] = data.libraryMaterials || [];

    // Filtra i materiali basandosi sulla ricerca
    const filteredMaterials = useMemo(() => {
        let materials = libraryMaterials.filter((material) => !deletedMaterialIds.has(material.materialID));

        if (!searchQuery.trim()) {
            return materials;
        }

        const query = searchQuery.toLowerCase().trim();
        return materials.filter(
            (material: LibraryMaterial) => material.name.toLowerCase().includes(query) || material.materialID.toLowerCase().includes(query)
        );
    }, [libraryMaterials, searchQuery, deletedMaterialIds]);

    const handleMaterialClick = useCallback((materialId: string) => {
        setSelectedMaterials((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(materialId)) {
                newSet.delete(materialId);
            } else {
                newSet.add(materialId);
            }
            return newSet;
        });
    }, []);

    const handleImport = useCallback(async () => {
        const selectedMaterialsData = filteredMaterials.filter((material: LibraryMaterial) => selectedMaterials.has(material.id));

        setIsImporting(true);

        if (sceneEditor && sceneEditor.importLibraryMaterial) {
            // Prima recupera i dettagli completi di ogni materiale selezionato via GraphQL
            const detailedMaterials = await Promise.all(
                selectedMaterialsData.map(async (material) => {
                    const detailedData: any = await fetchQuery(environment, libraryMaterialQuery, { libraryMaterialId: material.materialID }).toPromise();

                    return detailedData.libraryMaterial;
                })
            );

            // Poi per ognuno trasforma i dati nel formato corretto e importa
            detailedMaterials.forEach((detailedMaterial: any, index: number) => {
                if (detailedMaterial) {
                    // Crea Material usando il constructor diretto - il più semplice!
                    const zkMaterial = new (window as any).Zakeke.Material(detailedMaterial);

                    sceneEditor.importLibraryMaterial(zkMaterial);
                }
            });

            // PATTERN OBJECTEDITOR: dopo l'import, aggiorna i materiali come fa prepareModel
            const updatedMaterials = sceneEditor.getAllMaterials();

            if (onMaterialsUpdated) {
                onMaterialsUpdated(updatedMaterials);
            }

            // Chiama la callback passando i materiali con dettagli completi
            onImport(detailedMaterials.filter(Boolean));
        } else {
            console.warn('⚠️ sceneEditor.importLibraryMaterial not available, using fallback');
            onImport(selectedMaterialsData);
        }

        setIsImporting(false);
        // Non chiamiamo più closeDialog qui - è gestito dal chiamante
    }, [selectedMaterials, filteredMaterials, sceneEditor, onImport, onMaterialsUpdated]);

    const handleDelete = useCallback(async () => {
        const selectedMaterialsData = filteredMaterials.filter((material: LibraryMaterial) => selectedMaterials.has(material.id));

        if (selectedMaterialsData.length === 0) return;

        // La stessa forma della conferma per togliere dalla scena (`confirmRemoval`): titolo, la domanda,
        // l'azione rossa nel piede; si rinuncia dalla X.
        showDialog(
            'delete-confirmation-dialog',
            <Dialog
                title={T._('Delete from library', 'Admin')}
                size='small'
                closeOnEscape
                onClose={() => closeDialog('delete-confirmation-dialog')}
                buttons={[
                    {
                        label: T._('Delete', 'Admin'),
                        destructive: true,
                        onClick: async () => {
                            closeDialog('delete-confirmation-dialog');

                            // Mostra notifica "deleting selected library materials"
                            const deleteNotificationId = showInfoNotification({
                                message: T._('Deleting selected library materials...', 'Admin')
                            });

                            try {
                                // Elimina ogni materiale usando la mutation GraphQL
                                for (const material of selectedMaterialsData) {
                                    await new Promise<void>((resolve, reject) => {
                                        commitMutation(environment, {
                                            mutation: deleteLibraryMaterialMutation,
                                            variables: {
                                                deleteLibraryMaterialId: material.materialID
                                            },
                                            onCompleted: (response: any) => {
                                                // Aggiorna immediatamente lo stato locale
                                                setDeletedMaterialIds((prev) => new Set([...prev, material.materialID]));

                                                resolve();
                                            },
                                            onError: (error: any) => {
                                                console.error(`❌ Error deleting material ${material.name}:`, error);
                                                reject(error);
                                            }
                                        });
                                    });
                                }

                                // Aggiorna la lista dei materiali dopo l'eliminazione

                                // Rimuovi la notifica di progresso
                                removeNotification(deleteNotificationId);

                                // Forza il refresh della lista aggiornando il refreshKey
                                setRefreshKey((prev) => prev + 1);

                                // Reset selezione
                                setSelectedMaterials(new Set());

                                // Mostra notification di successo
                                showSuccessNotification({
                                    message: T._('Deleted selected library materials', 'Admin')
                                });
                            } catch (error) {
                                // Rimuovi la notifica di progresso
                                removeNotification(deleteNotificationId);

                                console.error('Error during delete process:', error);
                                showErrorNotification({
                                    message: stringFormat(T._('Error deleting materials: {0}', 'Admin'), (error as Error).message)
                                });
                            }
                        }
                    }
                ]}
            >
                <p>
                    {selectedMaterialsData.length === 1
                        ? stringFormat(T._('Delete “{0}” from the material library?', 'Admin'), selectedMaterialsData[0].name)
                        : stringFormat(T._('Delete {0} materials from the material library?', 'Admin'), selectedMaterialsData.length.toString())}
                </p>
            </Dialog>
        );
    }, [selectedMaterials, filteredMaterials, showDialog, closeDialog]);

    return (
        // UN ELENCO, NON UNA GALLERIA (utente, 2026-10-14): una riga per materiale, quindi la finestra
        // piccola basta. I comandi stanno nel piede e contano la scelta; si rinuncia dalla X.
        <Dialog
            title={T._('Import materials from library', 'Admin')}
            size='small'
            onClose={() => closeDialog('import-materials')}
            buttons={[
                {
                    label: stringFormat(T._('Delete selected ({0})', 'Admin'), selectedMaterials.size.toString()),
                    outline: true,
                    disabled: selectedMaterials.size === 0 || isImporting,
                    onClick: handleDelete
                },
                {
                    label: isImporting ? T._('Importing...', 'Admin') : stringFormat(T._('Import selected ({0})', 'Admin'), selectedMaterials.size.toString()),
                    disabled: selectedMaterials.size === 0 || isImporting,
                    onClick: handleImport
                }
            ]}
        >
            <SearchContainer>
                <SearchInput
                    type='text'
                    placeholder={T._('Search materials...', 'Admin')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </SearchContainer>

            <MaterialsListContainer>
                {filteredMaterials.map((material: LibraryMaterial) => (
                    <MaterialItem key={material.id} $selected={selectedMaterials.has(material.id)} onClick={() => handleMaterialClick(material.id)}>
                        <MaterialPreviewImage
                            src={material.imageUrl || '/assets/images/others/product-configurator.png'}
                            alt={material.name}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = '/assets/images/others/product-configurator.png';
                            }}
                        />
                        <MaterialName title={`${material.name} · ${material.materialID}`}>{material.name}</MaterialName>
                    </MaterialItem>
                ))}
            </MaterialsListContainer>
        </Dialog>
    );
};

export default MaterialImportDialog;
